import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export function useTablePreferences(tableId: string, defaultWidths: Record<string, number>) {
    const { user } = useAuth();
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>(defaultWidths);
    const [loading, setLoading] = useState(true);

    // Initial fetch
    useEffect(() => {
        if (!user) return;

        const fetchPreferences = async () => {
            try {
                const { data, error } = await supabase
                    .from('profiles')
                    .select('preferences')
                    .eq('id', user.id)
                    .single();

                if (error) throw error;

                if (data?.preferences?.[tableId]) {
                    setColumnWidths(prev => ({
                        ...prev,
                        ...data.preferences[tableId]
                    }));
                }
            } catch (error) {
                console.error('Error fetching table preferences:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPreferences();
    }, [user, tableId]);

    // Debounced save
    useEffect(() => {
        if (!user || loading) return;

        const timer = setTimeout(async () => {
            try {
                // First get current preferences to merge
                const { data: currentData } = await supabase
                    .from('profiles')
                    .select('preferences')
                    .eq('id', user.id)
                    .single();

                const currentPrefs = currentData?.preferences || {};

                const { error } = await supabase
                    .from('profiles')
                    .update({
                        preferences: {
                            ...currentPrefs,
                            [tableId]: columnWidths
                        }
                    })
                    .eq('id', user.id);

                if (error) throw error;
            } catch (error) {
                console.error('Error saving table preferences:', error);
            }
        }, 1000); // 1 second debounce

        return () => clearTimeout(timer);
    }, [columnWidths, user, tableId, loading]);

    return { columnWidths, setColumnWidths, loading };
}
